import React, { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import { Calendar, MapPin, Share2 } from 'lucide-react';
import { registry } from '../registry';
import type { InvitationData, TemplateMetadata } from '../types';
import {
  extractCoupleNames,
  parseFlexibleDate,
} from '@/src/infrastructure/lib/weddingDataHelper';
import { ScratchReveal } from '../_shared/ScratchReveal';
import './theme.css';

const META: TemplateMetadata = {
  id: 'exp-scratch-std-goldensand',
  name: 'Golden Sand — Scratch Save the Date',
  occasionType: 'WEDDING',
  description:
    'Interactive Save-the-Date: scratch gold sand to reveal couple names and date, with countdown and share CTA.',
  isPremium: true,
  tags: ['wedding', 'save-the-date', 'scratch', 'interactive', 'canvas', 'teaser', 'premium'],
  colorScheme: { primary: '#C9A227', accent: '#5C3A1E', bg: '#FFF8E7' },
};

function pickStr(...vals: unknown[]): string {
  for (const v of vals) {
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return '';
}

function useCountdown(targetMs: number | null) {
  const [left, setLeft] = useState({ d: 0, h: 0, m: 0, s: 0 });
  useEffect(() => {
    if (targetMs == null || Number.isNaN(targetMs)) return;
    const tick = () => {
      const diff = targetMs - Date.now();
      const next =
        diff <= 0
          ? { d: 0, h: 0, m: 0, s: 0 }
          : {
              d: Math.floor(diff / 86400000),
              h: Math.floor((diff / 3600000) % 24),
              m: Math.floor((diff / 60000) % 60),
              s: Math.floor((diff / 1000) % 60),
            };
      // Skip no-op updates so interval ticks don't force re-renders every second
      // when the displayed units are unchanged (also avoids Date-identity effect storms).
      setLeft((prev) =>
        prev.d === next.d && prev.h === next.h && prev.m === next.m && prev.s === next.s
          ? prev
          : next,
      );
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [targetMs]);
  return left;
}

export function ScratchStdGoldensand({ data }: { data: InvitationData }) {
  const expMeta = (data.customData as { expMeta?: Record<string, unknown> } | null)?.expMeta ?? {};
  const wedding = data.customData?.wedding;
  const extracted = extractCoupleNames(data.eventName);

  const groomName = pickStr(
    wedding?.groom?.fullName,
    wedding?.groom?.nickname,
    expMeta.groomName,
    extracted?.groom,
  );
  const brideName = pickStr(
    wedding?.bride?.fullName,
    wedding?.bride?.nickname,
    expMeta.brideName,
    extracted?.bride,
  );
  const coupleTitle =
    groomName && brideName ? `${groomName} & ${brideName}` : groomName || brideName || data.eventName || '';

  const parsedDate = parseFlexibleDate(data.eventDate);
  const eventDateLabel = parsedDate
    ? parsedDate.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
    : pickStr(data.eventDate);

  const venue = pickStr(data.eventVenue);
  const hostName = pickStr(data.hostName);
  // Primitive ms — not the Date object — so the effect deps stay stable across renders.
  const countdown = useCountdown(parsedDate ? parsedDate.getTime() : null);

  const share = async () => {
    const text = `Save the Date — ${coupleTitle}${eventDateLabel ? ` · ${eventDateLabel}` : ''}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: 'Save the Date', text, url: window.location.href });
      } catch {
        /* user cancelled */
      }
    } else if (navigator.clipboard) {
      await navigator.clipboard.writeText(`${text} ${window.location.href}`);
    }
  };

  return (
    <div className="ssd-root" data-template="exp-scratch-std-goldensand">
      <section className="ssd-hero">
        <p className="ssd-eyebrow">Save the Date</p>
        <motion.h1 initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="ssd-title">
          {data.eventName || 'We are getting married'}
        </motion.h1>
        {hostName ? <p className="ssd-host">From {hostName}</p> : null}

        <ScratchReveal className="ssd-scratch" height={300}>
          <div className="ssd-reveal">
            <p className="ssd-couple">{coupleTitle || 'The Couple'}</p>
            {eventDateLabel ? (
              <p className="ssd-date">
                <Calendar size={16} /> {eventDateLabel}
              </p>
            ) : null}
            {venue ? (
              <p className="ssd-venue">
                <MapPin size={16} /> {venue}
              </p>
            ) : null}
          </div>
        </ScratchReveal>

        {parsedDate ? (
          <div className="ssd-countdown" aria-label="Countdown">
            {[
              ['Days', countdown.d],
              ['Hrs', countdown.h],
              ['Min', countdown.m],
              ['Sec', countdown.s],
            ].map(([label, value]) => (
              <div key={String(label)}>
                <strong>{value}</strong>
                <span>{label}</span>
              </div>
            ))}
          </div>
        ) : null}

        <button type="button" className="ssd-share" onClick={() => void share()}>
          <Share2 size={16} /> Share Save the Date
        </button>
      </section>
    </div>
  );
}

registry.register({ metadata: META, component: ScratchStdGoldensand });
